
import { useState, useEffect } from 'react';
import { OrderWithCustomer } from '../types/orderTypes';
import { useToast } from '@/components/ui/use-toast';
import { useOrderSelection } from './useOrderSelection';
import { useOrdersLoader } from './useOrdersLoader';
import { useOrderDeletion } from './useOrderDeletion';
import { formatDate, formatStatus, formatPaymentStatus } from '../utils/formatters';
import { supabase } from '@/lib/supabase/client';

interface UseOrdersDataProps {
  shopId?: string;
}

export const useOrdersData = ({ shopId }: UseOrdersDataProps = {}) => {
  const { orders, setOrders, loading, organizationId } = useOrdersLoader({ shopId });
  const { toast } = useToast();

  console.log('useOrdersData hook initializing with shopId:', shopId, 'organizationId:', organizationId);

  // Import order selection features
  const {
    selectedOrders,
    editingOrderId,
    isAdding,
    setIsAdding,
    toggleOrderSelection,
    toggleSelectAll,
    areAllSelected,
    handleEditOrder,
    handleBackToOrders
  } = useOrderSelection();

  // Reset order selection when shop changes
  useEffect(() => {
    if (shopId) {
      console.log("Shop ID changed, resetting order selection");
      // Pass undefined or empty array to clear all selections
      toggleSelectAll();
    }
  }, [shopId, toggleSelectAll]);

  // Import order deletion features
  const { handleDeleteOrder, handleBulkDeleteOrders } = useOrderDeletion(orders, setOrders);

  // Handle add order button click
  const handleAddOrder = () => {
    console.log('handleAddOrder called with shopId:', shopId, 'organizationId:', organizationId);
    setIsAdding(true);
  };

  // Handle order added
  const handleOrderAdded = async (newOrder: OrderWithCustomer) => {
    console.log('handleOrderAdded called with new order:', newOrder);
    
    // Récupérer le nom du client si ce n'est pas déjà inclus
    let orderWithCustomerName = { ...newOrder };
    
    if (!orderWithCustomerName.customer_name && orderWithCustomerName.customer_id) {
      try {
        const { data: customerData, error } = await supabase
          .from('customers')
          .select('name')
          .eq('id', orderWithCustomerName.customer_id)
          .eq('organization_id', organizationId) // Filter by organization
          .single();
          
        if (!error && customerData) {
          orderWithCustomerName.customer_name = customerData.name;
        } else {
          orderWithCustomerName.customer_name = 'Client inconnu';
          console.error('Erreur lors de la récupération du nom du client:', error);
        }
      } catch (err) {
        console.error('Erreur lors de la récupération du nom du client:', err);
        orderWithCustomerName.customer_name = 'Client inconnu';
      }
    }
    
    setOrders([orderWithCustomerName, ...orders]);
    setIsAdding(false);
    toast({
      title: "Succès",
      description: "Commande ajoutée avec succès",
    });
  };

  // Handle order updated
  const handleOrderUpdated = (updatedOrder: OrderWithCustomer) => {
    console.log('handleOrderUpdated called with updated order:', updatedOrder);
    setOrders(orders.map(order => 
      order.id === updatedOrder.id ? updatedOrder : order
    ));
    toast({
      title: "Succès",
      description: "Commande mise à jour avec succès",
    });
  };

  return {
    orders,
    loading,
    selectedOrders,
    editingOrderId,
    isAdding,
    formatDate,
    formatStatus,
    formatPaymentStatus,
    handleAddOrder,
    handleEditOrder,
    handleOrderUpdated,
    handleOrderAdded,
    handleDeleteOrder,
    handleBulkDeleteOrders,
    handleBackToOrders,
    toggleOrderSelection,
    toggleSelectAll,
    areAllSelected,
    shopId,
    organizationId
  };
};
